import { useEffect, createContext, useContext, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

interface PostHogContextType {
  isEnabled: boolean;
}

const PostHogContext = createContext<PostHogContextType>({ isEnabled: false });

export const usePostHogContext = () => useContext(PostHogContext);

interface PostHogProviderProps {
  children: ReactNode;
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  const location = useLocation();
  
  // Check if current route is admin
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isEnabled = Boolean(POSTHOG_KEY) && !isAdminRoute;

  useEffect(() => {
    if (!POSTHOG_KEY) {
      console.log('PostHog: No API key configured, skipping initialization');
      return;
    }

    // Initialize PostHog once
    if (!posthog.__loaded) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: 'identified_only',
        capture_pageview: false, // We handle this manually via PostHogPageTracker
        capture_pageleave: true,
        autocapture: {
          dom_event_allowlist: ['click', 'submit'],
          url_allowlist: [], // Will be filtered by sanitize_properties
        },
        sanitize_properties: (properties) => {
          // Suppress all tracking on admin routes
          const currentPath = window.location.pathname;
          if (currentPath.startsWith('/admin')) {
            return null;
          }
          return properties;
        },
      });
    }
  }, []);

  // Opt out/in based on route
  useEffect(() => {
    if (!POSTHOG_KEY || !posthog.__loaded) return;

    if (isAdminRoute) {
      posthog.opt_out_capturing();
    } else {
      posthog.opt_in_capturing();
    }
  }, [isAdminRoute]);

  return (
    <PostHogContext.Provider value={{ isEnabled }}>
      {children}
    </PostHogContext.Provider>
  );
}
